import { isAxiosError } from 'axios'
import {
  AlertCircle,
  Edit,
  Image as ImageIcon,
  Plus,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { PopupFormModal } from '@/app/components/popups/PopupFormModal'
import { POPUPS_FRONTEND_PREVIEW_ENABLED } from '@/app/components/popups/popup-access'
import { popupFrequencyLabels } from '@/app/components/popups/popup-form'
import { ConfirmationModal } from '@/app/components/ui/ConfirmationModal'
import { GlobalFilterBar } from '@/app/components/ui/GlobalFilterBar'
import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { GlobalPaginator } from '@/app/components/ui/global-paginator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import { TableSkeleton } from '@/app/components/ui/table-skeleton'
import {
  type PopupApiError,
  type PopupFrequency,
  type PopupLifecycleStatus,
  type StorePopup,
  popupService,
} from '@/app/services/popup.service'
import { useSessionStore } from '@/app/store/auth/auth.store'

const itemsPerPage = 15

const lifecycleLabels: Record<PopupLifecycleStatus, string> = {
  active: 'Activo',
  scheduled: 'Programado',
  expired: 'Expirado',
  inactive: 'Inactivo',
}

const lifecycleClasses: Record<PopupLifecycleStatus, string> = {
  active: 'bg-[#DCFCE6] text-[#008438]',
  scheduled: 'bg-blue-100 text-blue-700',
  expired: 'bg-slate-100 text-slate-700',
  inactive: 'bg-[#FFE2E2] text-[#C10008]',
}

const dateFormatter = new Intl.DateTimeFormat('es-MX', {
  dateStyle: 'medium',
})

function formatDate(value: string | null): string {
  if (!value) return 'Sin límite'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date)
}

function errorMessage(error: unknown, fallback: string): string {
  return isAxiosError<PopupApiError>(error)
    ? error.response?.data.message || fallback
    : fallback
}

export function Popups() {
  const canAction = useSessionStore((state) => state.canAction)
  const canCreate = POPUPS_FRONTEND_PREVIEW_ENABLED || canAction('online-store.popup.create')
  const canEdit = POPUPS_FRONTEND_PREVIEW_ENABLED || canAction('online-store.popup.edit')
  const canChangeStatus = POPUPS_FRONTEND_PREVIEW_ENABLED || canAction('online-store.popup.status')
  const canDelete = POPUPS_FRONTEND_PREVIEW_ENABLED || canAction('online-store.popup.delete')

  const [popups, setPopups] = useState<StorePopup[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({ total: 0, from: 0, to: 0 })
  const [search, setSearch] = useState('')
  const [frequency, setFrequency] = useState<PopupFrequency | ''>('')
  const [lifecycleStatus, setLifecycleStatus] = useState<PopupLifecycleStatus | ''>('')

  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [statusTarget, setStatusTarget] = useState<StorePopup | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<StorePopup | null>(null)
  const [pendingAction, setPendingAction] = useState<'status' | 'delete' | null>(null)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setLoadError(false)

    void popupService
      .getPopups({
        search: search || undefined,
        show_frequency: frequency || undefined,
        lifecycle_status: lifecycleStatus || undefined,
        page: currentPage,
        per_page: itemsPerPage,
      })
      .then((response) => {
        if (!active) return
        setPopups(response.data)
        setPagination({
          total: response.total,
          from: response.from ?? 0,
          to: response.to ?? 0,
        })
      })
      .catch(() => {
        if (!active) return
        setPopups([])
        setPagination({ total: 0, from: 0, to: 0 })
        setLoadError(!POPUPS_FRONTEND_PREVIEW_ENABLED)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [currentPage, frequency, lifecycleStatus, refreshKey, search])

  const refresh = () => setRefreshKey((value) => value + 1)
  const resetPage = () => setCurrentPage(1)

  const openCreate = () => {
    setEditingId(null)
    setFormOpen(true)
  }

  const openEdit = (popup: StorePopup) => {
    setEditingId(popup.id)
    setFormOpen(true)
  }

  const executeStatusChange = async () => {
    if (!statusTarget) return
    setPendingAction('status')
    try {
      await popupService.updateStatus(statusTarget.id, !statusTarget.is_active)
      toast.success('Estado del popup actualizado')
      setStatusTarget(null)
      refresh()
    } catch (error) {
      toast.error(errorMessage(error, 'No fue posible actualizar el popup'))
    } finally {
      setPendingAction(null)
    }
  }

  const executeDelete = async () => {
    if (!deleteTarget) return
    setPendingAction('delete')
    try {
      await popupService.deletePopup(deleteTarget.id)
      toast.success('Popup eliminado exitosamente')
      setDeleteTarget(null)
      if (popups.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1)
      } else {
        refresh()
      }
    } catch (error) {
      toast.error(errorMessage(error, 'No fue posible eliminar el popup'))
    } finally {
      setPendingAction(null)
    }
  }

  const hasFilters = Boolean(search || frequency || lifecycleStatus)

  const clearFilters = () => {
    setSearch('')
    setFrequency('')
    setLifecycleStatus('')
    resetPage()
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-header-foreground text-2xl font-semibold">Popups y emergentes</h1>
          <p className="text-muted-foreground mt-1 text-sm">Administra los mensajes visuales de la tienda en línea.</p>
        </div>
        {canCreate && (
          <Button type="button" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" /> Nuevo popup
          </Button>
        )}
      </div>

      <GlobalFilterBar
        show={['search']}
        values={{ search }}
        onChange={(changes) => {
          if (changes.search !== undefined) setSearch(changes.search)
          resetPage()
        }}
        customOptions={{ searchPlaceholder: 'Buscar por nombre...' }}
      >
        <Select
          value={frequency || 'all'}
          onValueChange={(value) => {
            setFrequency(value === 'all' ? '' : (value as PopupFrequency))
            resetPage()
          }}
        >
          <SelectTrigger aria-label="Filtrar por frecuencia">
            <SelectValue placeholder="Todas las frecuencias" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las frecuencias</SelectItem>
            {Object.entries(popupFrequencyLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={lifecycleStatus || 'all'}
          onValueChange={(value) => {
            setLifecycleStatus(value === 'all' ? '' : (value as PopupLifecycleStatus))
            resetPage()
          }}
        >
          <SelectTrigger aria-label="Filtrar por estado">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="scheduled">Programados</SelectItem>
            <SelectItem value="expired">Expirados</SelectItem>
            <SelectItem value="inactive">Inactivos</SelectItem>
          </SelectContent>
        </Select>

        {hasFilters && <Button type="button" variant="outline" onClick={clearFilters}>Limpiar filtros</Button>}
      </GlobalFilterBar>

      {isLoading ? (
        <TableSkeleton
          headers={['Popup', 'Frecuencia', 'Orden', 'Vigencia', 'Estado', 'Acciones']}
          columns={['text-two-lines', 'badge', 'text', 'text-two-lines', 'badge', 'actions']}
          rows={5}
        />
      ) : loadError ? (
        <div className="border-border bg-card rounded-lg border p-10 text-center">
          <AlertCircle className="text-destructive mx-auto mb-3 h-8 w-8" />
          <p className="font-medium">No fue posible cargar los popups</p>
          <p className="text-muted-foreground mt-1 text-sm">Revisa la conexión e intenta nuevamente.</p>
          <Button type="button" variant="outline" className="mt-4" onClick={refresh}>
            <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
          </Button>
        </div>
      ) : (
        <div className="border-border bg-table-bg overflow-hidden rounded-lg border shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px]">
              <thead className="border-border bg-table-header-bg border-b">
                <tr>
                  {['Popup', 'Frecuencia', 'Orden', 'Vigencia', 'Estado', 'Acciones'].map((header) => (
                    <th key={header} className="text-table-header-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase last:text-right">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {popups.length ? popups.map((popup) => (
                  <tr key={popup.id} className="hover:bg-table-row-hover">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="border-border bg-muted h-14 w-20 shrink-0 overflow-hidden rounded border">
                          <img src={popup.image_url} alt="" className="h-full w-full object-cover" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-table-foreground max-w-64 truncate font-medium">{popup.name}</p>
                          {popup.call_to_action_url && <p className="text-muted-foreground mt-1 max-w-64 truncate text-xs">{popup.call_to_action_url}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <Badge variant="secondary">{popupFrequencyLabels[popup.show_frequency]}</Badge>
                      {popup.show_frequency === 'EVERY_N_DAYS' && <p className="text-muted-foreground mt-1 text-xs">Después de {popup.show_again_after_days} días</p>}
                    </td>
                    <td className="text-table-foreground px-4 py-4 text-sm">{popup.sort_order}</td>
                    <td className="text-table-foreground px-4 py-4 text-xs">
                      <p>Desde: {formatDate(popup.starts_at)}</p>
                      <p className="text-muted-foreground mt-1">Hasta: {formatDate(popup.ends_at)}</p>
                    </td>
                    <td className="px-4 py-4">
                      <Badge className={lifecycleClasses[popup.lifecycle_status]}>{lifecycleLabels[popup.lifecycle_status]}</Badge>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canChangeStatus && (
                          <button type="button" onClick={() => setStatusTarget(popup)} title={popup.is_active ? 'Desactivar' : 'Activar'} className="text-primary hover:bg-primary/10 rounded p-1.5">
                            {popup.is_active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                          </button>
                        )}
                        {canEdit && (
                          <button type="button" onClick={() => openEdit(popup)} title="Editar" className="text-table-foreground hover:bg-muted rounded p-1.5">
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button type="button" onClick={() => setDeleteTarget(popup)} title="Eliminar" className="rounded p-1.5 text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <ImageIcon className="text-muted-foreground mx-auto mb-3 h-8 w-8" />
                      <p className="text-muted-foreground">No se encontraron popups</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pagination.total > 0 && (
            <GlobalPaginator
              totalItems={pagination.total}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              from={pagination.from}
              to={pagination.to}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      <PopupFormModal
        open={formOpen}
        popupId={editingId}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setEditingId(null)
        }}
        onSuccess={() => {
          setFormOpen(false)
          setEditingId(null)
          refresh()
        }}
      />

      <ConfirmationModal
        isOpen={statusTarget !== null}
        onClose={() => setStatusTarget(null)}
        onConfirm={executeStatusChange}
        preset="toggleStatus"
        itemName="popup"
        isLoading={pendingAction === 'status'}
      />
      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={executeDelete}
        preset="delete"
        itemName="popup"
        isLoading={pendingAction === 'delete'}
      />
    </div>
  )
}
